// Content-context wiring for the client-side ack reconciler.
//
// Binds the context-agnostic sweep driver (wa-communication-layer/message/
// ack-reconciler) to the concrete content-side plumbing:
//
//   - getState : the content Redux store (holds DBUserMessageReducer).
//   - resolve  : the EXISTING page ack bridge (getWAMessageAckData) — reads the
//                message out of the page-context WhatsApp store by its persisted
//                waMessageKey. That page handler does a participant-agnostic
//                in-store scan and returns { ack: null, error } when the message
//                is not present, so it NEVER force-loads (skip-if-absent).
//   - report   : promotePendingAck — the SAME backend call the live change:ack
//                handler (handleWaEvent, receivedMessageAckUpdate) uses to promote
//                sent_pending_ack → confirmed. Keyed by the durable
//                userMessageId (doc.id), NOT the reload-dropped pending-ack
//                registry — decoding survives a reload, the registry does not.
//
// The reconciler is a resilient DRIVER for the same upstream pipeline the live
// listener feeds; the backend ack merge is monotonic, so a concurrent live-push
// and a sweep-push are a harmless no-op.

import store from '@extension/context/common/store/extension-store';
import { getSerialized } from '@extension/wa-communication-layer/core/utils/wa-key-serialization';
import { promotePendingAck } from '@extension/wa-communication-layer/api/api-calls';
import { ACK } from '@bt-common/schemas/wa/message-ack';
import { AckState, readStoreAckState } from '@extension/wa-communication-layer/message/ack-state';
import { UnconfirmedDoc, StateWithUserMessages } from '@extension/wa-communication-layer/message/select-unconfirmed';
import type { WaMessageAckEntry } from '@extension/wa-communication-layer/message/message-retrieval-operations';
import {
  ReportLedger,
  SweepDeps,
  startAckReconciler as startDriver,
} from '@extension/wa-communication-layer/message/ack-reconciler';
import { ContentMessageServiceActions } from './contentMessageServiceActions';

/**
 * Resolve a doc's current WhatsApp store ack via the existing page ack bridge.
 * Returns null (skip) when the doc carries no usable waMessageKey or when the
 * page reports the message is not in the local store — the reconciler never
 * force-loads.
 */
export async function resolveStoreAck(doc: UnconfirmedDoc): Promise<AckState | null> {
  const waMessageKey = getSerialized(doc.waMessageKey);
  if (!waMessageKey) return null;

  const rows: WaMessageAckEntry[] = await ContentMessageServiceActions.getWAMessageAckData([
    { waMessageKey, messageId: doc.id },
  ]);
  const entry: WaMessageAckEntry | undefined = rows[0];
  if (!entry) return null;

  // The bridge stamps { ack: null, error } when the message isn't in the local
  // store — treat that as skip-if-absent. `entry.ack` is a REQUIRED number|null
  // on WaMessageAckEntry, so this guard is now type-checked against the real
  // shape (the pre-fix bug: the page path emitted no `ack` field at all, so this
  // guard tripped on `undefined` for every hit → confirmed=0).
  if (entry.ack == null || entry.error) return null;

  return readStoreAckState(entry);
}

/**
 * Push a confirmed monotonic advance upstream. Uses the durable userMessageId
 * (doc.id) so promotion works after a reload — the whole point of the
 * reconciler. Best-effort: a failed promote is retried on the next sweep (the
 * doc stays unconfirmed until the backend confirms it).
 */
export async function reportAck(doc: UnconfirmedDoc, state: AckState): Promise<void> {
  if (state.ack < ACK.SERVER) return;
  await promotePendingAck(doc.id, state.ack).catch(() => {
    /* best-effort; next sweep retries */
  });
}

/**
 * In-memory {@link ReportLedger}: the highest ack we've reported upstream for
 * each messageId this session. Keeps the reconciler from re-sending an identical
 * ack every sweep when the local dbReducer doc never advances after our promote.
 */
function createReportLedger(): ReportLedger {
  const highestByDoc = new Map<string, number>();
  return {
    lastAck: (messageId) => highestByDoc.get(messageId) ?? -Infinity,
    record: (messageId, ack) => {
      const prev = highestByDoc.get(messageId) ?? -Infinity;
      if (ack > prev) highestByDoc.set(messageId, ack);
    },
    retain: (currentIds) => {
      for (const id of highestByDoc.keys()) {
        if (!currentIds.has(id)) highestByDoc.delete(id);
      }
    },
  };
}

let started = false;

/**
 * Mount the ack reconciler in the content context. Idempotent — safe to call
 * once from the content-message-service init (alongside the real-time ack
 * handler). Optional deps overrides are for tests only.
 */
export function startContentAckReconciler(overrides?: Partial<SweepDeps>): void {
  if (started && !overrides) return;
  started = true;

  const deps: SweepDeps = {
    getState: (): StateWithUserMessages | undefined => store.getState(),
    resolve: resolveStoreAck,
    report: reportAck,
    ledger: createReportLedger(),
    onError: (context, error) => {
      console.warn(`[AckReconciler] ${context} error:`, error);
    },
    ...overrides,
  };

  startDriver(deps);
}
